import React from 'react';
import styled from 'styled-components';
import { Body7, Container, TextField } from '@ui';
import { FileChipRemoveButton } from './styles';

// A chip editor for a catalog item's aliases. Enter adds the typed alias (trimmed, deduped), the
// × on a chip removes it. The whole list is what PATCH /catalog/items/:code expects, so the editor
// owns the array and hands it back on every change.

export const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

// An alias is a source label, not a code, so it is drawn as text rather than with the monospace
// low-contrast `Chip` the codes use. Shared with the drawer's read-only list, so an alias looks the
// same whether or not it can be removed.
export const AliasChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 8px;
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 6px;
  color: ${({ theme }) => theme.themeColors.text};
`;

// The file chip's remove control, at chip size.
const RemoveButton = styled(FileChipRemoveButton)`
  width: 16px;
  height: 16px;
  font-size: 13px;
  line-height: 1;
`;

export const normalizeAlias = (value) => String(value || '').trim();

// Adds `candidate` to `aliases` unless blank or already present (case-insensitive).
export const addAlias = (aliases, candidate) => {
  const next = normalizeAlias(candidate);
  if (!next) return aliases;
  const lower = next.toLowerCase();
  if (aliases.some((alias) => alias.toLowerCase() === lower)) return aliases;
  return [...aliases, next];
};

export const AliasEditor = ({ aliases = [], onChange, label = 'Aliases', hint, id }) => {
  const [draft, setDraft] = React.useState('');

  const commit = () => {
    const next = addAlias(aliases, draft);
    if (next !== aliases) onChange(next);
    setDraft('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Backspace' && draft === '' && aliases.length > 0) {
      // Backspace on an empty field pops the last chip, the usual chip-editor gesture.
      e.preventDefault();
      onChange(aliases.slice(0, -1));
    }
  };

  const remove = (alias) => onChange(aliases.filter((a) => a !== alias));

  return (
    <div>
      <TextField
        id={id}
        label={label}
        value={draft}
        onChange={setDraft}
        onKeyDown={handleKeyDown}
        onBlur={commit}
        placeholder="Type an alias and press Enter"
        hint={hint || 'Press Enter to add. Source labels that should map to this item.'}
      />
      {aliases.length > 0 && (
        <Container top={8}>
          <ChipRow role="list" aria-label={label}>
            {aliases.map((alias) => (
              <AliasChip key={alias} role="listitem">
                <Body7>{alias}</Body7>
                <RemoveButton aria-label={`Remove alias ${alias}`} onClick={() => remove(alias)}>
                  <span aria-hidden="true">×</span>
                </RemoveButton>
              </AliasChip>
            ))}
          </ChipRow>
        </Container>
      )}
      {aliases.length === 0 && (
        <Container top={4}>
          <Body7 isLowContrast>No aliases yet.</Body7>
        </Container>
      )}
    </div>
  );
};
